import { FC, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useOktaAuth } from '@okta/okta-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import {
	Box,
	Button,
	ColumnLayout,
	Container,
	Form,
	FormField,
	Alert,
	Header,
	Input,
	Select,
	SpaceBetween,
	Table
} from '@cloudscape-design/components'
import { useClusterApprovalFormStore } from '../../../stores/clusters/approvalFormStore'
import useAcceptCluster from '../../../hooks/mutate/useAcceptCluster'
import useSingleAccount from '../../../hooks/query/useSingleAccount'
import useSingleTags from '../../../hooks/query/useSingleTags'
import useClusters from '../../../hooks/query/useClusters'
import useEksClusterVersions from '../../../hooks/query/useEksClusterVersions'
import type { ClusterApprovalRequest } from '../../../types/cluster'
import type { Cluster } from '../../../types/cluster'
import type { Account } from '../../../types/account'
import { getClusterNetworkFromAccount } from '../../../helper/clusterAccountNetwork'
import { resolveClusterPortfolioPrefix } from '../../../helper/clusterWorkloadRepo'

interface ClusterApprovalFormProps {
	cluster: Cluster
	shouldShowApprovalForm: (show: boolean) => void
}

const ClusterApprovalForm: FC<ClusterApprovalFormProps> = ({
	cluster,
	shouldShowApprovalForm
}) => {
	const { authState } = useOktaAuth()
	const thisUserEmail = (authState?.idToken?.claims?.email as string) ?? ''

	const {
		formValues,
		actions: { setFormValues, clearFormValues }
	} = useClusterApprovalFormStore()

	const { control, trigger, getValues, setValue, formState: { isValid } } = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues as { workloadClusterVersion: string }
	})

	/**
	 * The approval store is a module-level singleton and was only reset after a successful
	 * approve, so a form abandoned on one cluster seeded `useForm` defaultValues on the next
	 * one (a stale Workload Cluster Version that only a hard refresh cleared). Reset on unmount
	 * so every approval form starts from the store's initial values.
	 */
	useEffect(() => {
		return () => {
			clearFormValues()
		}
	}, [clearFormValues])

	const queryClient = useQueryClient()
	const clusterPk = cluster.workloadRepoNameEnv ?? cluster.clusterID
	const { mutate: acceptCluster, isLoading: isAcceptLoading } = useAcceptCluster(clusterPk)
	const { data: account } = useSingleAccount(cluster?.details?.distributionList ?? '', false)
	const { data: tags } = useSingleTags(cluster?.tagsID ?? '', {
		enabled: !!cluster?.tagsID
	})
	const { data: clustersList } = useClusters()
	const {
		data: eksVersions = [],
		isLoading: eksVersionsLoading,
		isError: eksVersionsError
	} = useEksClusterVersions()
	const [approveError, setApproveError] = useState('')

	const application = cluster?.details?.application ?? ''
	const environment = cluster?.details?.environment ?? ''
	const sgEnvironment = ({ dv: 'dev', st: 'stg', pd: 'prd' } as Record<string, string>)[environment] ?? environment
	const accountId = (account as { accountID?: string })?.accountID ?? cluster?.details?.accountID ?? ''
	const portfolioPrefix = useMemo(
		() =>
			resolveClusterPortfolioPrefix({
				portfolio: cluster?.details?.portfolio,
				distributionList: cluster?.details?.distributionList,
				tagProject: (tags?.details as Record<string, unknown> | undefined)?.tagProject
			}),
		[cluster?.details?.portfolio, cluster?.details?.distributionList, tags?.details]
	)

	const { vpcName: vpcValue, privateHostedZoneName: dnsZoneApex } = useMemo(
		() =>
			getClusterNetworkFromAccount(
				account as Account | null | undefined,
				environment,
				application,
				{
					portfolioPrefix,
					systemAlias: application,
					distributionList: cluster?.details?.distributionList,
					accountType: account?.type,
					isStgFlow: environment === 'st'
				}
			),
		[account, environment, portfolioPrefix, application, cluster?.details?.distributionList]
	)

	const isProductionEnv = environment === 'pd'
	const isStagingEnv = environment === 'st'

	const approvedDvClusterExists = useMemo(() => {
		if (!isProductionEnv && !isStagingEnv) return true
		const clusterName = (cluster?.details?.clusterName ?? '').trim()
		if (!clusterName || !clustersList?.length) return false
		return (clustersList as Cluster[]).some(
			(c) =>
				(c.details?.clusterName ?? '').trim() === clusterName &&
				(c.details?.environment ?? '').toLowerCase() === 'dv' &&
				c.status === 'APPROVED'
		)
	}, [isProductionEnv, isStagingEnv, cluster?.details?.clusterName, clustersList])

	const stageNetworkApproved =
		(account as { stgRequest?: { status?: string } })?.stgRequest?.status === 'APPROVED'

	const stPrerequisitesMet = !isStagingEnv || (approvedDvClusterExists && stageNetworkApproved)

	const eksVersionsReady = !eksVersionsLoading && !eksVersionsError && eksVersions.length > 0
	const approveBlocked =
		!isValid ||
		!eksVersionsReady ||
		!stPrerequisitesMet ||
		(isProductionEnv && !approvedDvClusterExists)

	useEffect(() => {
		if (!eksVersions.length) return
		const current = String(getValues('workloadClusterVersion') ?? '').trim()
		if (current && eksVersions.includes(current)) return
		setValue('workloadClusterVersion', eksVersions[0], { shouldValidate: true })
		setFormValues('workloadClusterVersion', eksVersions[0])
	}, [eksVersions, getValues, setValue, setFormValues])

	const defaultSecurityGroups = useMemo(
		() => [
			portfolioPrefix
				? `${portfolioPrefix}-${application}-${sgEnvironment}-default-sg-1`
				: `${application}-${sgEnvironment}-default-sg-1`,
			portfolioPrefix
				? `${portfolioPrefix}-${application}-${sgEnvironment}-default-sg-2`
				: `${application}-${sgEnvironment}-default-sg-2`,
			portfolioPrefix
				? `${portfolioPrefix}-${application}-${sgEnvironment}-default-sg-3`
				: `${application}-${sgEnvironment}-default-sg-3`
		],
		[portfolioPrefix, application, sgEnvironment]
	)
	const [additionalSecurityGroups, setAdditionalSecurityGroups] = useState<string[]>([])

	const envSuffix = isProductionEnv ? 'PRD' : 'STG'
	const securityAccountId = isProductionEnv ? '713010294205' : '085094121557'
	const defaultTfProviders = useMemo(
		() => [
			{ id: '1', alias: 'environment', region: 'ap-southeast-1', arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-${envSuffix}`, externalId: `GitlabRunner-426956141294-${accountId}` },
			{ id: '2', alias: 'route53', region: 'ap-southeast-1', arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-${envSuffix}`, externalId: `GitlabRunner-426956141294-${accountId}` },
			{ id: '3', alias: 'dr', region: 'ap-southeast-2', arn: `arn:aws:iam::${accountId}:role/AC2-IRS-DO-GLI-BL-${accountId}-${envSuffix}`, externalId: `GitlabRunner-426956141294-${accountId}` },
			{ id: '4', alias: 'security', region: 'ap-southeast-1', arn: `arn:aws:iam::${securityAccountId}:role/AC2-IRS-DO-GLD-SECURITY-${envSuffix}`, externalId: 'atlas' },
			{ id: '5', alias: 'security_dr', region: 'ap-southeast-2', arn: `arn:aws:iam::${securityAccountId}:role/AC2-IRS-DO-GLD-SECURITY-${envSuffix}`, externalId: 'atlas' },
			{ id: '6', alias: 'workload', region: 'ap-southeast-1', arn: `arn:aws:secretsmanager:ap-southeast-1:${accountId || '713010294205'}:secret:gitlab/projects/cluster_manifest_workload_token`, externalId: '' },
			{ id: '7', alias: 'sharedservices', region: 'ap-southeast-1', arn: 'arn:aws:iam::337994588368:role/AC2-IRS-DO-GLD-ATLAS_SHARED_SERVICES-PRD', externalId: 'atlas' }
		],
		[accountId, envSuffix, securityAccountId]
	)
	type TfProviderItem = { id: string; alias: string; region: string; arn: string; externalId: string }
	const [additionalTfProviders, setAdditionalTfProviders] = useState<TfProviderItem[]>([])
	const tfProvidersItems = useMemo(
		() => [...defaultTfProviders, ...additionalTfProviders],
		[defaultTfProviders, additionalTfProviders]
	)

	const watchedWorkloadVersion = useWatch({ control, name: 'workloadClusterVersion', defaultValue: '' })
	const workloadClusterVersionDisplay = String(watchedWorkloadVersion ?? '')

	const formValuesOutput = useMemo(
		() => ({
			workloadClusterVersion: workloadClusterVersionDisplay,
			vpcAliases: { identifier: 'tag:Name', value: vpcValue },
			dnsZoneApex,
			securityGroups: [...defaultSecurityGroups, ...additionalSecurityGroups.filter(Boolean)],
			tfProviders: tfProvidersItems.map(({ alias, region, arn, externalId }) => ({ alias, region, arn, externalId }))
		}),
		[
			workloadClusterVersionDisplay,
			vpcValue,
			dnsZoneApex,
			defaultSecurityGroups,
			additionalSecurityGroups,
			tfProvidersItems
		]
	)

	const handleApprove = () => {
		trigger()
		if (approveBlocked) return

		const values = getValues() as Record<string, string>
		const workloadClusterVersion = String(values.workloadClusterVersion ?? '').trim()
		const approvalRequest: ClusterApprovalRequest = {
			clusterID: clusterPk,
			action: 'APPROVE',
			approverID: thisUserEmail,
			details: {
				...formValuesOutput,
				workloadClusterVersion
			}
		}

		setApproveError('')
		acceptCluster(approvalRequest, {
			onSuccess: (data) => {
				if (data?.error) {
					setApproveError(String(data.error))
					return
				}
				shouldShowApprovalForm(false)
				clearFormValues()
				// Optimistically show approved state (refetch may still run)
				queryClient.setQueryData(['cluster', clusterPk], {
					...cluster,
					status: 'APPROVED' as const
				})
			}
		})
	}

	return (
		<Form
			actions={
				<SpaceBetween direction="horizontal" size="xs">
					<Button variant="link" onClick={() => shouldShowApprovalForm(false)}>
						Cancel
					</Button>
					<Button
						variant="primary"
						loading={isAcceptLoading}
						onClick={handleApprove}
						disabled={approveBlocked}
					>
						Approve
					</Button>
				</SpaceBetween>
			}
		>
			<Container header={<Header variant="h2">Cluster Approval Details:</Header>}>
				<SpaceBetween direction="vertical" size="l">
					{isProductionEnv && !approvedDvClusterExists && (
						<Alert type="error" header="Production cluster onboarding">
							Production cluster onboarding requires an approved development (dv) cluster
							for workload repo{' '}
							<strong>{cluster?.details?.clusterName ?? 'this workload repo'}</strong>.
							Submit and approve the dv cluster first.
						</Alert>
					)}
					{isStagingEnv && !approvedDvClusterExists && (
						<Alert type="error" header="Staging cluster onboarding">
							Staging (st) cluster onboarding requires an approved development (dv) cluster
							for workload repo{' '}
							<strong>{cluster?.details?.clusterName ?? 'this workload repo'}</strong>.
							Submit and approve the dv cluster first.
						</Alert>
					)}
					{isStagingEnv && approvedDvClusterExists && !stageNetworkApproved && (
						<Alert type="error" header="Stage Network Details required">
							Staging (st) cluster onboarding requires Stage Network Details to be
							approved on the account before approval.
						</Alert>
					)}
					<FormField
						label="VPC Aliases"
					>
						<ColumnLayout columns={2} variant="text-grid">
							<div>
								<Box variant="awsui-key-label">VPC Identifier</Box>
								<Input value="tag:Name" readOnly disabled />
							</div>
							<div>
								<Box variant="awsui-key-label">VPC Value</Box>
								<Input value={vpcValue} readOnly disabled />
							</div>
						</ColumnLayout>
					</FormField>

					<FormField
						label="DNS Zone Apex"
					>
						<Input value={dnsZoneApex} readOnly disabled />
					</FormField>

					<FormField
						label="Default Security Groups"
					>
						<SpaceBetween direction="vertical" size="s">
							{defaultSecurityGroups.map((sg, idx) => (
								<Input key={`default-${idx}`} value={sg} readOnly disabled />
							))}
							{additionalSecurityGroups.map((sg, idx) => (
								<div key={`additional-${idx}`} style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
									<div style={{ flex: 1, minWidth: 0, width: '100%' }}>
										<Input
											value={sg}
											onChange={({ detail }) => {
												setAdditionalSecurityGroups((prev) => {
													const next = [...prev]
													next[idx] = detail.value
													return next
												})
											}}
											placeholder="Security group name or ARN"
										/>
									</div>
									<Button
										variant="inline-icon"
										iconName="remove"
										ariaLabel="Remove security group"
										onClick={() =>
											setAdditionalSecurityGroups((prev) =>
												prev.filter((_, i) => i !== idx)
											)
										}
									>
										Remove
									</Button>
								</div>
							))}
							<Box margin={{ top: 's' }}>
								<Button
									variant="normal"
									onClick={() =>
										setAdditionalSecurityGroups((prev) => [...prev, ''])
									}
								>
									Add Security Groups
								</Button>
							</Box>
						</SpaceBetween>
					</FormField>

					<FormField
						label="TF Providers"
					>
						<Table
							columnDefinitions={[
								{
									id: 'alias',
									header: 'Alias',
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return e.alias
										return (
											<Input
												value={e.alias}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) => (p.id === e.id ? { ...p, alias: detail.value } : p))
													)
												}
												placeholder="e.g. environment"
											/>
										)
									}
								},
								{
									id: 'region',
									header: 'Region',
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return e.region
										return (
											<Input
												value={e.region}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) => (p.id === e.id ? { ...p, region: detail.value } : p))
													)
												}
												placeholder="e.g. ap-southeast-1"
											/>
										)
									}
								},
								{
									id: 'arn',
									header: 'ARN',
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return e.arn
										return (
											<Input
												value={e.arn}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) => (p.id === e.id ? { ...p, arn: detail.value } : p))
													)
												}
												placeholder="arn:aws:iam::..."
											/>
										)
									}
								},
								{
									id: 'externalId',
									header: 'External ID',
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return e.externalId || '—'
										return (
											<Input
												value={e.externalId}
												onChange={({ detail }) =>
													setAdditionalTfProviders((prev) =>
														prev.map((p) => (p.id === e.id ? { ...p, externalId: detail.value } : p))
													)
												}
												placeholder="External ID"
											/>
										)
									}
								},
								{
									id: 'actions',
									header: '',
									width: 100,
									cell: (e) => {
										const isAdditional = e.id.startsWith('add-')
										if (!isAdditional) return null
										return (
											<Button
												variant="inline-icon"
												iconName="remove"
												ariaLabel="Remove provider"
												onClick={() =>
													setAdditionalTfProviders((prev) => prev.filter((p) => p.id !== e.id))
												}
											>
												Remove
											</Button>
										)
									}
								}
							]}
							items={tfProvidersItems}
							empty="No providers"
						/>
						<Box margin={{ top: 's' }}>
							<Button
								variant="normal"
								onClick={() =>
									setAdditionalTfProviders((prev) => [
										...prev,
										{ id: `add-${Date.now()}`, alias: '', region: '', arn: '', externalId: '' }
									])
								}
							>
								Add Providers
							</Button>
						</Box>
					</FormField>

					{eksVersionsError && (
						<Alert type="error" header="EKS versions unavailable">
							Workload cluster versions could not be loaded from AWS. Approval stays
							closed until the standard-support versions are available.
						</Alert>
					)}
					{approveError && (
						<Alert type="error" header="Cluster approval was not saved">
							{approveError}
						</Alert>
					)}
					<Controller
						name="workloadClusterVersion"
						control={control}
						rules={{
							validate: (value) => {
								if (eksVersionsLoading) return true
								if (!eksVersions.length) return 'EKS versions could not be loaded from AWS'
								if (!String(value ?? '').trim()) return 'Workload cluster version is required'
								return (
									eksVersions.includes(String(value)) ||
									'Select an EKS version in standard support'
								)
							}
						}}
						render={({ field, fieldState }) => (
							<FormField
								label="Workload Cluster Version"
								errorText={fieldState.error?.message}
							>
								<Select
									selectedOption={
										field.value
											? { label: String(field.value), value: String(field.value) }
											: null
									}
									options={eksVersions.map((version) => ({
										label: version,
										value: version
									}))}
									placeholder={
										eksVersionsLoading
											? 'Loading EKS versions from AWS…'
											: 'Select version'
									}
									statusType={
										eksVersionsLoading ? 'loading' : eksVersionsError ? 'error' : 'finished'
									}
									empty="No EKS versions in standard support"
									disabled={eksVersionsLoading || eksVersionsError}
									onChange={({ detail }) => {
										const value = String(detail.selectedOption?.value ?? '')
										field.onChange(value)
										setFormValues('workloadClusterVersion', value)
										setApproveError('')
									}}
								/>
							</FormField>
						)}
					/>
				</SpaceBetween>
			</Container>
		</Form>
	)
}

export default ClusterApprovalForm
